import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  useTheme,
  Avatar,
  Icons,
  Separator,
  Stack,
} from '@ublend-npm/aulaui-core';
import TimeAgo from 'react-timeago';
import * as NotificationType from '@core/constants/notificationTypes';
import { ANNOUNCEMENTS } from '@core/constants/postFeedType';
import {
  groupNotificationsByFilterType,
  hasUnreadNotifications,
  isUnread,
  notificationFilters,
  hasNewNotificationOfType,
} from '@core/utils/notificationFilters';
import push from '../../../utils/routing';
import { EntityTypesByCollections } from '../../../../core/utils/commentsEntityTypes';
import getCommentEntityUrl from '../../../../core/utils/getCommentEntityUrl';

import {
  StyledNotificationsContainer,
  StyledHeaderContainer,
  StyledHeaderLeft,
  StyledHeaderRight,
  StyledNotificationsTitle,
  StyledFilterContainer,
  StyledTabs,
  StyledTab,
  StyledBadge,
  StyledContentArea,
  StyledLoadingContainer,
  StyledLoader,
  StyledEmptyStateContainer,
  StyledEmptyStateText,
  StyledEmptyStateText2,
  StyledNotificationItem,
  StyledUnreadIndicator,
  StyledNotificationContent,
  StyledNotificationDetails,
  StyledNotificationHeader,
  StyledUserName,
  StyledActionText,
  StyledSpaceText,
  StyledEducatorChip,
  StyledMetaInfo,
  StyledTimeAgo,
  StyledContentPreview,
  StyledActionButton,
  StyledEmptyStateLink,
  StyledTooltip,
} from './NotificationsDialog.styled';
import { getCurrentSpaceId } from '@core/selectors/space';
import { useSelector } from 'react-redux';

const timeFormatter = (value, unit, suffix) => {
  if (value <= 60 && unit === 'second') {
    return 'Just Now';
  }
  let unitPluralized = unit;
  if (value !== 1) {
    unitPluralized += 's';
  }
  return `${value} ${unitPluralized} ${suffix}`;
};

const NotificationsLayout = ({
  notifications = [],
  notificationTypeFilter = 'All',
  changeNotificationTypeFilter,
  setRead,
  getNotifications,
  onClose,
  isFetchingNotifications = false,
}) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState('All');
  const currentSpaceId = useSelector(getCurrentSpaceId);
  const [initialLoadingStarted, setInitialLoadingStarted] = useState(false);
  const [notificationTypesRead, setNotificationTypesRead] = useState({
    [NotificationType.ALL]: true,
  });

  const [groupedNotifications, setGroupedNotifications] = useState(
    groupNotificationsByFilterType(notifications)
  );

  // Focus management for keyboard navigation
  const containerRef = React.useRef(null);

  useEffect(() => {
    // Reset unread status if there are new notifications
    const newGroupedNotifications =
      groupNotificationsByFilterType(notifications);

    if (
      hasNewNotificationOfType(
        newGroupedNotifications,
        groupedNotifications,
        NotificationType.ALL
      )
    ) {
      const nextNotificationsTypesRead = notificationFilters.reduce(
        (accum, filterType) => ({
          ...accum,
          ...(hasNewNotificationOfType(
            groupedNotifications,
            newGroupedNotifications,
            filterType
          ) &&
            notificationTypeFilter !== filterType && {
              [filterType]: false,
            }),
        }),
        notificationTypesRead
      );

      setNotificationTypesRead(nextNotificationsTypesRead);
    }
  }, [
    notifications,
    groupedNotifications,
    notificationTypeFilter,
    notificationTypesRead,
  ]);

  useEffect(() => {
    setGroupedNotifications(groupNotificationsByFilterType(notifications));
  }, [notifications]);

  const filteredNotifications =
    groupedNotifications[notificationTypeFilter]?.filter(
      (n) => !!n.notification.actionData
    ) || [];
  const unreadFilteredNotifications = filteredNotifications.filter(isUnread);

  const onOpenNotificationsSettings = () => {
    push(`/dashboard/${currentSpaceId}/profile/notifications`);
    onClose();
  };

  const handleClickNotification = (id) => {
    onClose();
    const notificationData = notifications.find(
      ({ notification: { itemId } }) => itemId === id
    );

    if (!notificationData) return;

    const { id: notificationId, notification } = notificationData;
    const { action, itemId, parentItemId, spaceId, parentItemType } =
      notification;

    // Mark as read
    setRead([{ id: notificationId }]);

    // Navigate to the appropriate URL based on action type
    // Use setTimeout to ensure the menu closes before navigation
    setTimeout(() => {
      switch (action) {
        case 'newPost': {
          push(`dashboard/${spaceId}/community/post/${itemId}`);
          break;
        }
        case 'newCommentOnCommentedPost':
        case 'newComment':
        case 'newCommentReply': {
          push(
            getCommentEntityUrl({
              spaceId,
              entityType: EntityTypesByCollections[parentItemType],
              entityId: parentItemId,
            })
          );
          break;
        }
        default:
          break;
      }
    }, 100);
  };

  const handleMarkSingleAsRead = (event, id) => {
    event.stopPropagation();
    const notificationData = notifications.find(
      ({ notification: { itemId } }) => itemId === id
    );
    if (notificationData) {
      setRead([{ id: notificationData.id }]);
    }
  };

  const handleMarkAllAsRead = () => {
    const unreadNotificationsIds = unreadFilteredNotifications.map(({ id }) => {
      return { id };
    });
    setRead(unreadNotificationsIds);
  };

  const handleSelectFilterType = (type) => {
    setTabValue(type);
    setNotificationTypesRead({
      ...notificationTypesRead,
      ...{ [type]: true },
    });
    changeNotificationTypeFilter(type);
  };

  // Handle keyboard navigation within the modal
  const handleKeyDown = (event) => {
    // Handle VoiceOver navigation (Ctrl+Option+Arrow keys)
    if (
      event.ctrlKey &&
      event.altKey &&
      (event.key === 'ArrowRight' || event.key === 'ArrowLeft')
    ) {
      event.preventDefault();
      event.stopPropagation();

      const container = containerRef.current;
      if (!container) return;

      // Get the actual DOM element (in case it's wrapped)
      let containerElement = container;
      if (container && !container.querySelectorAll) {
        containerElement = document.querySelector(
          '[data-notifications-container]'
        );
      }

      if (containerElement && containerElement.querySelectorAll) {
        // Get all focusable elements in proper order (including unread indicators)
        // Enhanced selector to ensure unread indicators are included
        const focusableElements = containerElement.querySelectorAll(
          'button:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), [role="button"]:not([disabled]), [role="status"], [data-focusable="true"]'
        );

        if (focusableElements && focusableElements.length > 0) {
          const currentIndex = Array.from(focusableElements).indexOf(
            document.activeElement
          );

          let nextIndex;
          if (event.key === 'ArrowRight') {
            // Forward navigation
            nextIndex =
              currentIndex >= focusableElements.length - 1
                ? 0
                : currentIndex + 1;
          } else {
            // Backward navigation
            nextIndex =
              currentIndex <= 0
                ? focusableElements.length - 1
                : currentIndex - 1;
          }

          const nextElement = focusableElements[nextIndex];
          if (nextElement) {
            nextElement.focus();

            // For VoiceOver, ensure the element is properly announced
            if (nextElement.hasAttribute('data-focusable')) {
              // Add a small delay to ensure VoiceOver picks up the focus change
              setTimeout(() => {
                nextElement.setAttribute('aria-live', 'polite');
                setTimeout(() => {
                  nextElement.removeAttribute('aria-live');
                }, 100);
              }, 50);
            }
          }
        }
      }
      return;
    }

    // Prevent default tab behavior to manage focus within modal
    if (event.key === 'Tab') {
      // Always prevent default and stop propagation for Tab key to prevent modal from closing
      event.preventDefault();
      event.stopPropagation();

      // Use document.querySelector to find focusable elements within the container
      const container = containerRef.current;
      if (!container) return;

      // Get the actual DOM element (in case it's wrapped)
      let containerElement = container;
      if (container && !container.querySelectorAll) {
        // If it's not a DOM element, try to find the DOM element
        containerElement = document.querySelector(
          '[data-notifications-container]'
        );
      }

      if (containerElement && containerElement.querySelectorAll) {
        const focusableElements = containerElement.querySelectorAll(
          'button:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), [role="button"]:not([disabled]), [role="status"], [data-focusable="true"]'
        );

        if (focusableElements && focusableElements.length > 0) {
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];
          const currentIndex = Array.from(focusableElements).indexOf(
            document.activeElement
          );

          if (event.shiftKey) {
            // Shift + Tab (backward navigation)
            if (currentIndex <= 0) {
              lastElement.focus();
            } else {
              focusableElements[currentIndex - 1].focus();
            }
          } else {
            // Tab (forward navigation)
            if (currentIndex >= focusableElements.length - 1) {
              firstElement.focus();
            } else {
              focusableElements[currentIndex + 1].focus();
            }
          }
        }
      }
    }
  };

  useEffect(() => {
    if (isFetchingNotifications || initialLoadingStarted) {
      return;
    }
    setInitialLoadingStarted(true);
    getNotifications();
  }, [isFetchingNotifications, initialLoadingStarted, getNotifications]);

  // Set initial focus when modal opens
  useEffect(() => {
    // Focus the first focusable element when the component mounts
    const timer = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;

      // Get the actual DOM element (in case it's wrapped)
      let containerElement = container;
      if (container && !container.querySelectorAll) {
        // If it's not a DOM element, try to find the DOM element
        containerElement = document.querySelector(
          '[data-notifications-container]'
        );
      }

      if (containerElement && containerElement.querySelectorAll) {
        const focusableElements = containerElement.querySelectorAll(
          'button:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), [role="button"]:not([disabled]), [role="status"], [data-focusable="true"]'
        );
        if (focusableElements && focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const renderDescription = (action, data) => {
    switch (action) {
      case 'newPost':
        return `created a${
          data.feedType === ANNOUNCEMENTS ? 'n announcement' : ''
        } post`;
      case 'newComment':
        return `commented on your post`;
      case 'newCommentReply':
        return `replied to your comment`;
      case 'newCommentOnCommentedPost':
        return `commented on ${data.author}'s post`;
      default:
        return '';
    }
  };

  // Calculate total unread notifications
  const totalUnreadCount = notifications.filter(isUnread).length;

  return (
    <StyledNotificationsContainer
      theme={theme}
      ref={containerRef}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      data-notifications-container
    >
      <StyledHeaderContainer theme={theme}>
        <StyledHeaderLeft theme={theme}>
          <StyledNotificationsTitle variant="h6" theme={theme}>
            Notifications
          </StyledNotificationsTitle>
          {totalUnreadCount > 0 && (
            <>
              <StyledBadge
                theme={theme}
                role="status"
                badgeContent={totalUnreadCount}
                aria-label={`${totalUnreadCount} Unread Notifications`}
                aria-describedby="unread-count-description"
                aria-live="polite"
                aria-atomic="true"
                tabIndex={0}
              />
              {/* Hidden element for better VoiceOver support on mobile */}
              <span
                id="unread-count-description"
                style={{
                  position: 'absolute',
                  left: '-10000px',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                }}
                aria-live="polite"
                aria-atomic="true"
              >
                {totalUnreadCount} unread notifications
              </span>
            </>
          )}
        </StyledHeaderLeft>
        <StyledHeaderRight theme={theme}>
          <StyledTooltip
            title="Notification settings"
            position="bottom"
            theme={theme}
            arrow={true}
          >
            <div>
              <StyledActionButton
                size="small"
                onClick={onOpenNotificationsSettings}
                theme={theme}
                aria-label="Notification settings"
                tabIndex={0}
              >
                <Icons.SettingsOutlined />
              </StyledActionButton>
            </div>
          </StyledTooltip>
          <StyledTooltip
            title="Mark all as read"
            position="bottom"
            theme={theme}
            arrow={true}
          >
            <div>
              <StyledActionButton
                size="small"
                onClick={handleMarkAllAsRead}
                disabled={unreadFilteredNotifications.length === 0}
                theme={theme}
                aria-label="Mark all as read"
                tabIndex={0}
              >
                <Icons.DoneAllOutlined />
              </StyledActionButton>
            </div>
          </StyledTooltip>
        </StyledHeaderRight>
      </StyledHeaderContainer>

      {/* Filter Container with counts */}
      <StyledFilterContainer theme={theme}>
        <StyledTabs
          theme={theme}
          value={
            filteredNotifications.length === 0
              ? tabValue
              : notificationTypeFilter
          }
          aria-label="Notification filters"
        >
          {Object.keys(groupedNotifications).map((filterType) => {
            const hasUnread =
              hasUnreadNotifications(groupedNotifications[filterType]) &&
              !notificationTypesRead[filterType];
            return (
              <StyledTab
                key={`${filterType}-tab`}
                theme={theme}
                value={filterType}
                label={`${filterType}`}
                onClick={() => handleSelectFilterType(filterType)}
                selected={
                  (filteredNotifications.length === 0
                    ? tabValue
                    : notificationTypeFilter) === filterType
                }
                aria-label={`Filter by ${filterType} notifications`}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelectFilterType(filterType);
                  }
                }}
              />
            );
          })}
        </StyledTabs>
      </StyledFilterContainer>

      <Separator />

      {/* Content */}
      <StyledContentArea theme={theme}>
        {isFetchingNotifications ? (
          <StyledLoadingContainer theme={theme}>
            <StyledLoader theme={theme} />
          </StyledLoadingContainer>
        ) : filteredNotifications.length === 0 ? (
          <StyledEmptyStateContainer theme={theme}>
            <StyledEmptyStateText theme={theme}>
              No notifications
            </StyledEmptyStateText>
            <StyledEmptyStateText2 variant="body2" theme={theme}>
              You're all caught up! We'll let you know as soon as something
              important comes up—meanwhile, check your settings to make sure
              you're getting the notifications that matter most to you.
            </StyledEmptyStateText2>
            <StyledEmptyStateLink
              theme={theme}
              onClick={onOpenNotificationsSettings}
              aria-label="Open notification settings"
              style={{ fontFamily: 'Roboto, sans-serif' }}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpenNotificationsSettings();
                }
              }}
            >
              Notification settings
            </StyledEmptyStateLink>
          </StyledEmptyStateContainer>
        ) : (
          <Stack spacing={1}>
            {filteredNotifications.map(
              ({ id, createdAt, read, culprit, notification }) => (
                <StyledNotificationItem
                  key={id}
                  theme={theme}
                  isUnread={!read}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleClickNotification(notification.itemId);
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`${
                    !read ? 'Unread notification' : 'Notification'
                  } from ${notification.actionData.culprit}${
                    culprit.isInstructor ? ', Educator' : ''
                  }: ${renderDescription(
                    notification.action,
                    notification.actionData
                  )}${
                    notification.actionData.space
                      ? ` in ${notification.actionData.space}`
                      : ''
                  }. ${notification.content.replace(/<[^>]+>/g, ' ').trim()}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleClickNotification(notification.itemId);
                    }
                  }}
                >
                  {/* Unread indicator */}
                  {!read && (
                    <StyledUnreadIndicator
                      theme={theme}
                      onClick={(e) =>
                        handleMarkSingleAsRead(e, notification.itemId)
                      }
                      tabIndex={0}
                      role="button"
                      aria-label={`Mark notification from ${notification.actionData.culprit} as read`}
                      title={`Mark notification from ${notification.actionData.culprit} as read`}
                      data-focusable="true"
                      aria-hidden="false"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleMarkSingleAsRead(e, notification.itemId);
                        }
                      }}
                    />
                  )}

                  <StyledNotificationContent>
                    {/* Avatar */}
                    <Avatar
                      src={culprit.avatar}
                      fallbackText={`${
                        culprit?.firstName
                          ? culprit.firstName.charAt(0).toUpperCase()
                          : 'U'
                      }${
                        culprit?.lastName
                          ? culprit.lastName.charAt(0).toUpperCase()
                          : ''
                      }`.substring(0, 2)}
                      sx={{
                        width: 40,
                        height: 40,
                      }}
                    >
                      {notification.actionData.culprit?.charAt(0).toUpperCase()}
                    </Avatar>

                    {/* Content */}
                    <StyledNotificationDetails
                      id={`notification-${notification.itemId}-description`}
                    >
                      {/* User name and action */}
                      <StyledNotificationHeader variant="body1">
                        <StyledUserName component="span" theme={theme}>
                          {notification.actionData.culprit}
                        </StyledUserName>

                        {culprit.isInstructor && (
                          <StyledEducatorChip theme={theme}>
                            Educator
                          </StyledEducatorChip>
                        )}

                        <StyledActionText component="span" theme={theme}>
                          {renderDescription(
                            notification.action,
                            notification.actionData
                          )}
                        </StyledActionText>

                        {notification.actionData.space && (
                          <StyledSpaceText component="span" theme={theme}>
                            in {notification.actionData.space}
                          </StyledSpaceText>
                        )}
                      </StyledNotificationHeader>

                      {/* Meta information */}
                      <StyledMetaInfo>
                        <StyledTimeAgo variant="caption" theme={theme}>
                          <TimeAgo
                            date={createdAt}
                            minPeriod={60}
                            formatter={timeFormatter}
                          />
                        </StyledTimeAgo>
                      </StyledMetaInfo>

                      {/* Content preview */}
                      <StyledContentPreview variant="body1" theme={theme}>
                        {notification.content.replace(/<[^>]+>/g, ' ')}
                      </StyledContentPreview>
                    </StyledNotificationDetails>
                  </StyledNotificationContent>
                </StyledNotificationItem>
              )
            )}
          </Stack>
        )}
      </StyledContentArea>
    </StyledNotificationsContainer>
  );
};

NotificationsLayout.propTypes = {
  notifications: PropTypes.arrayOf(PropTypes.shape({})),
  getNotifications: PropTypes.func.isRequired,
  setRead: PropTypes.func.isRequired,
  onOpenNotificationsSettings: PropTypes.func.isRequired,
  notificationTypeFilter: PropTypes.string,
  changeNotificationTypeFilter: PropTypes.func.isRequired,
  isFetchingNotifications: PropTypes.bool,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  customNavBar: PropTypes.func,
};

export default NotificationsLayout;
